import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, MapPin, Briefcase, DollarSign, ArrowLeft, Loader2, MessageSquare, Printer } from 'lucide-react';
import JobChatbot from '@/components/JobChatbot';
import { PrintJDDialog } from '@/components/job';
import RichTextDisplay from '@/components/ui/rich-text-display';

interface JobSkill {
  skill_category: string | null;
  years_experience: number | null;
  skills: {
    id: string;
    name: string;
    type: string | null;
  };
}

const JobDetail = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatbotMode, setChatbotMode] = useState<'chat' | 'apply'>('chat');
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          clients (name, company_name),
          job_skills (
            skill_category,
            years_experience,
            skills (id, name, type)
          )
        `)
        .eq('id', jobId)
        .single();

      if (error) throw error;
      setJob(data);
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group skills by category
  const groupedSkills = {
    required_technical: [] as JobSkill[],
    good_to_have_technical: [] as JobSkill[],
    soft: [] as JobSkill[],
  };

  if (job?.job_skills) {
    job.job_skills.forEach((js: JobSkill) => {
      const category = js.skill_category || 'required_technical';
      if (category in groupedSkills) {
        groupedSkills[category as keyof typeof groupedSkills].push(js);
      }
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Job Not Found</h2>
          <p className="text-muted-foreground mb-4">This job posting may no longer be available.</p>
          <Link to="/jobs">
            <Button>Back to Jobs</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                RecruitAI
              </h1>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/jobs">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Jobs
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setShowPrintDialog(true)}>
            <Printer className="mr-2 h-4 w-4" />
            Print JD
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-bold mb-2">{job.title}</h1>
                {job.clients && !job.hide_client && (
                  <p className="text-xl text-primary">{job.clients.name}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b">
                {job.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-5 w-5" />
                    <span>{job.location}</span>
                  </div>
                )}
                {job.job_type && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-5 w-5" />
                    <span className="capitalize">{job.job_type.replace('_', ' ')}</span>
                  </div>
                )}
                {job.work_mode && (
                  <Badge variant="secondary" className="capitalize">
                    {job.work_mode.replace('_', ' ')}
                  </Badge>
                )}
                {!job.hide_salary && (job.salary_min || job.salary_max) && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="h-5 w-5" />
                    <span>
                      {job.salary_currency || 'USD'}{' '}
                      {job.salary_min && job.salary_max 
                        ? `${(job.salary_min / 1000).toFixed(0)}k - ${(job.salary_max / 1000).toFixed(0)}k`
                        : job.salary_min 
                        ? `From ${(job.salary_min / 1000).toFixed(0)}k`
                        : `Up to ${(job.salary_max / 1000).toFixed(0)}k`
                      }
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-3">Description</h2>
                  <RichTextDisplay content={job.description} />
                </div>

                {job.requirements && (
                  <div>
                    <h2 className="text-xl font-semibold mb-3">Requirements</h2>
                    <RichTextDisplay content={job.requirements} />
                  </div>
                )}

                {/* Skills Sections */}
                {groupedSkills.required_technical.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-3">Required Technical Skills</h2>
                    <div className="flex flex-wrap gap-2">
                      {groupedSkills.required_technical.map((js, i) => (
                        <Badge key={i} variant="default" className="flex items-center gap-1">
                          {js.skills.name}
                          {js.years_experience && js.years_experience > 0 && (
                            <span className="text-xs opacity-75">({js.years_experience}+ yrs)</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {groupedSkills.good_to_have_technical.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-3">Good to Have Technical Skills</h2>
                    <div className="flex flex-wrap gap-2">
                      {groupedSkills.good_to_have_technical.map((js, i) => (
                        <Badge key={i} variant="secondary">
                          {js.skills.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {groupedSkills.soft.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-3">Soft Skills</h2>
                    <div className="flex flex-wrap gap-2">
                      {groupedSkills.soft.map((js, i) => (
                        <Badge key={i} variant="outline">
                          {js.skills.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6 sticky top-24">
              <Button 
                size="lg" 
                className="w-full mb-4"
                onClick={() => {
                  setChatbotMode('apply');
                  setShowChatbot(true);
                }}
              >
                Apply Now
              </Button>
              
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full"
                onClick={() => {
                  setChatbotMode('chat');
                  setShowChatbot(true);
                }}
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Ask Questions
              </Button>

              <div className="mt-6 pt-6 border-t text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">About this role</p>
                <p>Our AI assistant can answer questions about this position and help you with your application.</p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Chatbot Modal */}
      {showChatbot && (
        <JobChatbot 
          jobId={job.id} 
          jobTitle={job.title}
          onClose={() => setShowChatbot(false)}
          initialMode={chatbotMode}
        />
      )}

      {/* Print Dialog */}
      <PrintJDDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        job={job}
        skills={{
          required_technical: groupedSkills.required_technical.map(js => ({
            id: js.skills.id,
            name: js.skills.name,
            years_experience: js.years_experience
          })),
          good_to_have_technical: groupedSkills.good_to_have_technical.map(js => ({
            id: js.skills.id,
            name: js.skills.name
          })),
          soft: groupedSkills.soft.map(js => ({
            id: js.skills.id,
            name: js.skills.name
          }))
        }}
      />
    </div>
  );
};

export default JobDetail;
